import { Popover } from '@libs/ui/ds/lib/components'
import { DatePickerCustomTargetWrap } from '@libs/ui/ds/lib/components/dates/DatePickerInput/specialized/DatePickerInput/subs/DatePickerCustomTargetWrap'
import { CalendarPicker } from '@libs/ui/ds/lib/components/dates/datePickerUtils/subs/CalendarPicker'
import { DatePickerFocusWrap } from '@libs/ui/ds/lib/components/dates/datePickerUtils/subs/DatePickerFocusWrap'
import { PickerPresetList } from '@libs/ui/ds/lib/components/dates/datePickerUtils/subs/PickerPresets/PickerPresetList'
import type { FC } from 'react'
import React from 'react'

import { dateConfig } from '../../../datePickerUtils/utils/dateConfig'
import { useDatePickerOpened } from '../../../datePickerUtils/utils/useDatePickerOpened/useDatePickerOpened'

import type { DatePickerInputProps } from './DatePickerInput.types'
import { DatePickerTargetInput } from './subs/targets/DatePickerTargetInput'
import { DatePickerTargetInputLike } from './subs/targets/DatePickerTargetInputLike'

const DatePickerInputBase: FC<DatePickerInputProps> = ({
  value,
  onChange,
  onBlur,
  defaultValue,
  presets,
  /** Popover props */
  onOpenedChange,
  opened,
  dropdownPosition = 'bottom-start',

  /** InputWrap Props */
  label,
  disabled = false,
  required,
  errorMessage,
  desc,
  readOnly = false,
  rightSection,
  onClear,
  clearable = false,
  pickerOnly,
  customTarget,

  /** Calendar Props */
  maxLevel,
  placeholder,
  excludeDate,
  minDate,
  maxDate,
  defaultDate,
  withWeekNumbers,
  ...props
}) => {
  const openedValues = useDatePickerOpened({ onOpenedChange, opened })
  const syncDate = dateConfig.useSyncDate({
    value,
    defaultValue,
    defaultDate,
    onChange,
    onBlur,
    minDate,
    maxDate
  })

  return (
    <Popover
      dropdownHeight='l'
      dropdownWidth='auto'
      dropdownPosition={dropdownPosition}
      opened={openedValues.opened}
      disabled={disabled || readOnly}
    >
      <DatePickerFocusWrap setOpen={openedValues.setOpened}>
        <DatePickerCustomTargetWrap
          customTarget={customTarget}
          errorMessage={errorMessage}
          rightSection={rightSection}
          placeholder={placeholder}
          pickerOnly={pickerOnly}
          clearable={clearable}
          required={required}
          syncDate={{
            date: syncDate.date,
            onChange: syncDate.onChange,
            onChangeStr: syncDate.onChangeStr,
            strValue: syncDate.strValue,
            value: syncDate.value,
            onDateChange: syncDate.onDateChange,
            reset: syncDate.reset,
            minDate: syncDate.minDate,
            maxDate: syncDate.maxDate,
            defaultDate: syncDate.defaultDate,
            errorMessage: syncDate.errorMessage
          }}
          onClear={onClear}
          label={label}
          desc={desc}
          disabled={disabled}
          {...props}
        />

        <Popover.Dropdown>
          <CalendarPicker
            value={syncDate.value}
            date={syncDate.date}
            onDateChange={syncDate.onDateChange}
            onChange={val => {
              openedValues.setOpened(false)
              syncDate.onChange(val)
            }}
            minDate={syncDate.minDate}
            maxDate={syncDate.maxDate}
            defaultDate={syncDate.defaultDate}
            type='default'
            componentType='date'
            numberOfColumns={1}
            maxLevel={maxLevel}
            excludeDate={excludeDate}
            withWeekNumbers={withWeekNumbers}
          />
          <PickerPresetList
            onChange={val => {
              syncDate.onChange(val)
            }}
            presets={presets}
            value={{ start: syncDate.strValue, end: syncDate.strValue }}
          />
        </Popover.Dropdown>
      </DatePickerFocusWrap>
    </Popover>
  )
}

export type { DatePickerInputProps } from './DatePickerInput.types'

type CompoundDatePickerInputType = {
  Targets: {
    Input: typeof DatePickerTargetInput
    InputLike: typeof DatePickerTargetInputLike
  }
}

const TypedDatePickerInput = DatePickerInputBase as typeof DatePickerInputBase &
  CompoundDatePickerInputType

TypedDatePickerInput.Targets = {
  Input: DatePickerTargetInput,
  InputLike: DatePickerTargetInputLike
}

TypedDatePickerInput.displayName = 'DatePickerInput'
export const DatePickerInput = TypedDatePickerInput
